import { useState, useMemo, useEffect } from "react";
import { pickNextAssignment } from "@/lib/nextAssignment";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Users,
  BarChart3,
  FileText,
  Radio,
  Clock,
  ExternalLink,
  Download,
  Loader2,
  CheckCircle2,
  Music,
  Layers,
  Pencil,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ProductionSidebar from "@/components/ProductionSidebar";
import CoProducerManager from "@/components/CoProducerManager";
import ScheduleFeedbackDashboardCard from "@/components/ScheduleFeedbackDashboardCard";
import { format, eachDayOfInterval, parseISO, differenceInDays } from "date-fns";
import { useSidebar } from "@/contexts/SidebarContext";
import { PageTransition } from "@/components/PageTransition";
import { SchedulePreviewCard } from "@/components/SchedulePreviewCard";
import { LiveTrackingPreviewCard } from "@/components/LiveTrackingPreviewCard";
import { ScheduleExportModal } from "@/components/schedule/ScheduleExportModal";
import { toast } from "sonner";
import { EditProductionFAB } from "@/components/EditProductionFAB";
import { useAuth } from "@/contexts/AuthContext";
import { useMinuteTick } from "@/hooks/useMinuteTick";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { dedupeByCrewIdentity } from "@produce/shared";
import {
  assignmentCountQuery,
  briefingQuery,
  crewMembersQuery,
  crewWithAssignmentsQuery,
  lineupCountQuery,
} from "@/lib/queries/productionQueries";

const ProductionDashboard = () => {
  // Honours the producer's 12h/24h setting — this rendered a raw HH:MM cut.
  const formatTime = useProducerTimeFormatter();
  const { productionId } = useParams();
  const navigate = useNavigate();
  const { expanded } = useSidebar();
  const { userId, isCoProducerOnly } = useAuth();
  const queryClient = useQueryClient();
  const [scheduleExportOpen, setScheduleExportOpen] = useState(false);
  const [isDownloadingBriefing, setIsDownloadingBriefing] = useState(false);

  // Drives the "Next up" recompute on the crew sidebar (minute-boundary
  // ticks that survive a throttled background tab — see useMinuteTick).
  const now = useMinuteTick();

  // Realtime: invalidate the crew widget's assignments cache when any
  // crew_assignment row for this production changes. The page-level
  // QueryClient defaults disable refetchOnWindowFocus / -Reconnect and
  // set staleTime to 5 min, so without this subscription a producer
  // who edited the lineup or assignments in another tab would see a
  // stale "Next up" until they navigated away and back. crew_assignments
  // is already in the supabase_realtime publication (see migration
  // 20251111120130). Filter on briefing_id keeps each open dashboard
  // listening to only its own production.
  useEffect(() => {
    if (!productionId) return;
    const channel = supabase
      .channel(`production-dashboard-assignments-${productionId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "crew_assignments",
          filter: `briefing_id=eq.${productionId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["crew-with-assignments", productionId],
          });
          queryClient.invalidateQueries({
            queryKey: ["assignment-count", productionId],
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [productionId, queryClient]);

  // Fetch production details
  const { data: production, isLoading } = useQuery({
    ...briefingQuery(productionId),
    enabled: !!productionId && !!userId,
  });

  // Fetch all crew members (raw data for deduplication)
  // One crew read for both the stats below and the schedule export. It used
  // to be two identical queries under two cache keys.
  const { data: rawCrewMembers = [] } = useQuery({
    ...crewMembersQuery(productionId),
    enabled: !!productionId,
  });
  const allCrewMembers = rawCrewMembers;

  // Deduplicate crew members by canonical identity — the shared rule
  // (briefing_crew_id → user_id → lowercased email), so the count on this card
  // and the crew list below it can never disagree about who is one person.
  const uniqueCrewMembers = useMemo(() => dedupeByCrewIdentity(rawCrewMembers), [rawCrewMembers]);

  // Fetch crew members with their next assignments (deduplicated)
  const { data: crewWithAssignments = [] } = useQuery({
    ...crewWithAssignmentsQuery(productionId),
    enabled: !!productionId,
  });

  // Fetch assignment count
  const { data: assignmentCount = 0 } = useQuery({
    ...assignmentCountQuery(productionId),
    enabled: !!productionId,
  });

  // Fetch lineup artists count
  const { data: lineupCount = 0 } = useQuery({
    ...lineupCountQuery(productionId),
    enabled: !!productionId,
  });

  // Fetch recent timeline events
  const { data: _recentEvents = [] } = useQuery({
    queryKey: ["recent-events", productionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_timeline_events")
        .select("*")
        .eq("briefing_id", productionId!)
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
    enabled: !!productionId,
  });

  // Calculate event days for schedule export
  const eventDays =
    production?.festival_date_start && production?.festival_date_end
      ? eachDayOfInterval({
          start: parseISO(production.festival_date_start),
          end: parseISO(production.festival_date_end),
        }).map((date) => format(date, "yyyy-MM-dd"))
      : production?.festival_date_start
        ? [production.festival_date_start]
        : [];

  // Calculate stats
  const uniqueCrewCount = uniqueCrewMembers.length;
  const confirmedCrewCount = uniqueCrewMembers.filter((m) => m.user_id).length;
  const stageCount = Array.isArray(production?.stages) ? production.stages.length : 0;
  const eventDayCount =
    production?.festival_date_start && production?.festival_date_end
      ? differenceInDays(
          parseISO(production.festival_date_end),
          parseISO(production.festival_date_start),
        ) + 1
      : 1;

  const handleDownloadBriefing = async () => {
    if (!productionId || !production) return;

    setIsDownloadingBriefing(true);
    try {
      const { data, error } = await supabase.functions.invoke("export-briefing-pdf", {
        body: { productionId },
      });

      if (error) throw error;

      // Convert base64 to blob and download
      const byteCharacters = atob(data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `PROduce - ${production.festival_name} - Briefing.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Briefing PDF downloaded");
    } catch (error) {
      console.error(error);
      toast.error("Failed to download briefing PDF");
    } finally {
      setIsDownloadingBriefing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <ProductionSidebar productionId={productionId!} />
        <main className="flex flex-1 items-center justify-center bg-muted/20 p-8">
          <div className="text-muted-foreground">Loading production...</div>
        </main>
      </div>
    );
  }

  if (!production) {
    return (
      <div className="flex min-h-screen">
        <ProductionSidebar productionId={productionId!} />
        <main className="flex flex-1 items-center justify-center bg-muted/20 p-8">
          <div className="text-center">
            <h2 className="mb-2 text-xl font-semibold">Production not found</h2>
            <Button onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
          </div>
        </main>
      </div>
    );
  }

  const formatDateStr = (dateStr: string) => {
    return format(new Date(dateStr), "MMM dd, yyyy");
  };

  const isSingleDay = production.festival_date_start === production.festival_date_end;

  return (
    <div className="flex min-h-screen bg-background">
      <ProductionSidebar productionId={productionId!} />

      <PageTransition
        className="flex-1 transition-all duration-300"
        style={{ marginLeft: expanded ? "220px" : "72px" }}
      >
        {/* Header */}
        <div className="border-b border-border bg-background px-6 py-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold">{production.festival_name}</h1>
              <p className="text-sm text-muted-foreground">
                {production.location} •{" "}
                {isSingleDay
                  ? formatDateStr(production.festival_date_start!)
                  : `${formatDateStr(production.festival_date_start!)} - ${formatDateStr(production.festival_date_end!)}`}
              </p>
            </div>
            <Badge variant={production.status === "published" ? "default" : "secondary"}>
              {production.status === "published" ? "Published" : "Draft"}
            </Badge>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3">
            <Button variant="outline" asChild>
              <Link to={`/productions/${productionId}/briefing`}>
                <FileText className="mr-2 h-4 w-4" />
                Open briefing
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to={`/productions/${productionId}/crew`}>
                <Users className="mr-2 h-4 w-4" />
                Manage crew
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to={`/productions/${productionId}/analytics`}>
                <BarChart3 className="mr-2 h-4 w-4" />
                Open analytics
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to={`/productions/${productionId}/live-tracking`}>
                <Radio className="mr-2 h-4 w-4" />
                Live tracking
              </Link>
            </Button>

            {/* Download Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {isDownloadingBriefing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Download
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleDownloadBriefing} disabled={isDownloadingBriefing}>
                  <FileText className="mr-2 h-4 w-4" />
                  Briefing PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setScheduleExportOpen(true)}>
                  <Clock className="mr-2 h-4 w-4" />
                  Schedule PDF
                </DropdownMenuItem>
                <DropdownMenuItem disabled className="opacity-50">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Analytics
                  <Badge variant="secondary" className="ml-auto text-xs">
                    Soon
                  </Badge>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="p-8">
          {/* Widgets Grid - 2x2 with equal height */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Crew Widget */}
            <Card className="flex h-full flex-col border-border bg-card p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-lg font-semibold">
                  <Users className="h-5 w-5" />
                  The crew
                </h3>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/briefings/${productionId}/edit?step=3&shortcut=true`}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    Edit crew
                  </Link>
                </Button>
              </div>
              <div className="flex-1 space-y-3">
                {crewWithAssignments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No crew members added yet</p>
                ) : (
                  <>
                    {crewWithAssignments.map((member) => {
                      // Recomputed every minute via the `now` tick — fixes the
                      // bug where Next-up froze on the first assignment of the day.
                      const nextAssignment = pickNextAssignment(member.assignments, now);
                      return (
                        <div
                          key={member.email}
                          className="flex items-start justify-between border-b border-border py-2 last:border-0"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">{member.name}</p>
                            <p className="text-xs text-muted-foreground">{member.function}</p>
                          </div>
                          {nextAssignment && (
                            <div className="ml-2 text-right text-xs text-muted-foreground">
                              <p className="font-medium">Next up:</p>
                              <p>{nextAssignment.artist_name || nextAssignment.stage_name}</p>
                              <p className="flex items-center justify-end gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTime(nextAssignment.filming_start)}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              <Button
                variant="ghost"
                className="mt-auto w-full text-primary hover:text-primary"
                asChild
              >
                <Link to={`/productions/${productionId}/crew`}>
                  View all crew
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </Card>

            {/* Quick Stats Widget */}
            <Card className="flex h-full flex-col border-border bg-card p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-lg font-semibold">
                  <BarChart3 className="h-5 w-5" />
                  Quick stats
                </h3>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/briefings/${productionId}/edit?step=0`}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    Edit production
                  </Link>
                </Button>
              </div>
              <div className="grid flex-1 grid-cols-2 gap-4">
                {/* Crew count */}
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{uniqueCrewCount}</div>
                    <p className="text-xs text-muted-foreground">Crew members</p>
                  </div>
                </div>

                {/* Confirmation rate */}
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-green-500/10 p-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {confirmedCrewCount}/{uniqueCrewCount}
                    </div>
                    <p className="text-xs text-muted-foreground">Confirmed</p>
                  </div>
                </div>

                {/* Assignments */}
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-blue-500/10 p-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{assignmentCount}</div>
                    <p className="text-xs text-muted-foreground">Assignments</p>
                  </div>
                </div>

                {/* Event days */}
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-orange-500/10 p-2">
                    <Calendar className="h-4 w-4 text-orange-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{eventDayCount}</div>
                    <p className="text-xs text-muted-foreground">
                      Event {eventDayCount === 1 ? "day" : "days"}
                    </p>
                  </div>
                </div>

                {/* Stages */}
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-purple-500/10 p-2">
                    <Layers className="h-4 w-4 text-purple-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stageCount}</div>
                    <p className="text-xs text-muted-foreground">
                      {stageCount === 1 ? "Stage" : "Stages"}
                    </p>
                  </div>
                </div>

                {/* Lineup artists */}
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-pink-500/10 p-2">
                    <Music className="h-4 w-4 text-pink-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{lineupCount}</div>
                    <p className="text-xs text-muted-foreground">Lineup artists</p>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                className="mt-auto w-full text-primary hover:text-primary"
                asChild
              >
                <Link to={`/productions/${productionId}/analytics`}>
                  Open analytics
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </Card>

            {/* Schedule Preview Widget */}
            <div className="h-full">
              <SchedulePreviewCard
                productionId={productionId!}
                festivalDateStart={production.festival_date_start ?? ""}
                festivalDateEnd={production.festival_date_end}
              />
            </div>

            {/* Live Tracking Widget */}
            <div className="h-full">
              <LiveTrackingPreviewCard
                productionId={productionId!}
                navigationAddress={production.navigation_address}
              />
            </div>
          </div>

          {/* Open client feedback collected via shared schedule-review links. */}
          {productionId && (
            <div className="mt-6">
              <ScheduleFeedbackDashboardCard productionId={productionId} />
            </div>
          )}

          {/* Co-producers — owner-only; co-producers can't manage each other */}
          {!isCoProducerOnly && productionId && (
            <div className="mt-6">
              <CoProducerManager productionId={productionId} />
            </div>
          )}
        </div>
      </PageTransition>

      {/* Schedule Export Modal */}
      <ScheduleExportModal
        open={scheduleExportOpen}
        onOpenChange={setScheduleExportOpen}
        briefingId={productionId!}
        festivalName={production.festival_name || "Production"}
        eventDays={eventDays}
        crewMembers={allCrewMembers}
      />
      {productionId && <EditProductionFAB briefingId={productionId} />}
    </div>
  );
};

export default ProductionDashboard;
